import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { CALENDARS_BY_YEAR, YEARS, CABINS, DEFAULT_OCCUPANCY, dateKey } from "../data";
import {
  ArrowLeft,
  Save,
  RotateCcw,
  Anchor,
  Bell,
  Check,
  X,
  MessageCircle,
  Clock,
  Users,
  Mail,
  Phone,
  Lock,
  LogOut,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL = 20000; // 20s
const TOKEN_KEY = "apecatu_admin_token";

const getToken = () => {
  try { return localStorage.getItem(TOKEN_KEY) || ""; } catch { return ""; }
};
const setToken = (t) => {
  try { if (t) localStorage.setItem(TOKEN_KEY, t); else localStorage.removeItem(TOKEN_KEY); } catch { /* noop */ }
};
const authHeaders = () => {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const BED_COLORS = { free: "#1FA866", occupied: "#E05548" };

const flatDates = YEARS.flatMap((year) =>
  CALENDARS_BY_YEAR[year].flatMap((m) =>
    m.dates.map((d) => ({
      year,
      month: m.month,
      range: d.range,
      days: d.days,
      status: d.status,
      special: d.special,
      key: dateKey(m.month, d.range, year),
    }))
  )
);

const cabinNameByKey = {
  cabineCasal: "Cabine Casal",
  suiteTripla: "Suíte Tripla",
  suiteCasal: "Suíte Casal",
  cabineQuadrupla: "Cabine Quádrupla",
};

const emptyOcc = () => ({
  cabineCasal: [false, false],
  suiteTripla: [false, false, false],
  suiteCasal: [false, false],
  cabineQuadrupla: [false, false, false, false],
});

const emptyRestrictions = () => ({
  suiteTripla: null,
  cabineQuadruplaA: null,
  cabineQuadruplaB: null,
});

// ==================== Login Screen ====================
const LoginScreen = ({ onSuccess }) => {
  const [login, setLoginValue] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!login.trim() || !password.trim()) return;
    setError("");
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { login: login.trim(), password });
      if (res.data?.token) {
        setToken(res.data.token);
        toast.success("Bem-vindo(a) ao painel Apecatu");
        onSuccess?.();
      } else {
        setError("Resposta inválida do servidor.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login ou senha incorretos.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6"
      style={{
        background: "linear-gradient(135deg, #04111E 0%, #0F1B33 100%)",
        color: "#fff",
      }}
      data-testid="login-screen"
    >
      <Toaster position="top-right" richColors />
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div
            className="inline-flex items-center justify-center w-14 h-14 mb-4"
            style={{ background: "#0066B3", color: "#fff" }}
          >
            <Lock size={22} />
          </div>
          <div className="text-[11px] tracking-[0.32em] uppercase font-semibold" style={{ color: "#F5C754" }}>
            Painel Interno
          </div>
          <div className="text-3xl font-bold mt-2" style={{ letterSpacing: "-0.02em" }}>
            Apecatu · Abrolhos 2026/27
          </div>
          <div className="text-xs mt-2" style={{ color: "rgba(255,255,255,0.55)" }}>
            Acesso restrito à equipe.
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="p-6"
          style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", backdropFilter: "blur(12px)" }}
        >
          <label className="block text-[10px] uppercase tracking-wider font-semibold mb-2" style={{ color: "#F5C754" }}>
            Login
          </label>
          <input
            type="text"
            value={login}
            onChange={(e) => setLoginValue(e.target.value)}
            autoFocus
            autoComplete="username"
            spellCheck={false}
            className="w-full px-3 py-3 text-sm mb-4 outline-none"
            style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.15)", color: "#fff" }}
            data-testid="login-input"
          />

          <label className="block text-[10px] uppercase tracking-wider font-semibold mb-2" style={{ color: "#F5C754" }}>
            Senha
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            className="w-full px-3 py-3 text-sm mb-4 outline-none"
            style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.15)", color: "#fff" }}
            data-testid="password-input"
          />

          {error && (
            <div
              className="text-xs px-3 py-2 mb-4"
              style={{ background: "rgba(224,85,72,0.12)", color: "#FF8B7F", border: "1px solid rgba(224,85,72,0.35)" }}
              data-testid="login-error"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || !login.trim() || !password.trim()}
            className="w-full py-3 flex items-center justify-center gap-2 text-xs uppercase tracking-widest font-bold disabled:opacity-50"
            style={{ background: "#0066B3", color: "#fff" }}
            data-testid="login-submit"
          >
            {submitting ? "Entrando..." : "Entrar no painel"}
          </button>

          <div className="text-[10px] text-center mt-4" style={{ color: "rgba(255,255,255,0.35)" }}>
            <a href="/" className="hover:underline">← voltar para o site</a>
          </div>
        </form>
      </div>
    </div>
  );
};

const VagasAdmin = () => {
  const [authed, setAuthed] = useState(!!getToken());
  const [selectedKey, setSelectedKey] = useState(flatDates[0]?.key || "");
  const [adminYear, setAdminYear] = useState(flatDates[0]?.year || YEARS[0]);
  const [availability, setAvailability] = useState({});
  const [current, setCurrent] = useState(emptyOcc());
  const [currentRestr, setCurrentRestr] = useState(emptyRestrictions());
  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);
  const [tab, setTab] = useState("pending"); // pending | history | editor
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const prevPendingCount = useRef(0);
  const [textInput, setTextInput] = useState("");
  const [parseResult, setParseResult] = useState(null); // { aplicaveis, problemas }
  const [selected, setSelected] = useState(new Set());
  const [parsing, setParsing] = useState(false);
  const [applying, setApplying] = useState(false);

  const selectedDate = flatDates.find((d) => d.key === selectedKey);

  const handleAuthError = useCallback((e) => {
    if (e?.response?.status === 401 || e?.response?.status === 403) {
      setToken("");
      setAuthed(false);
      toast.error("Sessão expirada. Faça login novamente.");
      return true;
    }
    return false;
  }, []);

  const loadAvailability = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/availability`, { headers: authHeaders() });
      setAvailability(res.data || {});
    } catch (e) {
      handleAuthError(e);
    }
  }, [handleAuthError]);

  const loadReservations = useCallback(async (silent = false) => {
    try {
      const [p, a] = await Promise.all([
        axios.get(`${API}/reservations?status=pending`, { headers: authHeaders() }),
        axios.get(`${API}/reservations`, { headers: authHeaders() }),
      ]);
      const pendingList = p.data || [];
      const allList = a.data || [];
      const historyList = allList.filter((r) => r.status !== "pending");

      // Notify on new pending
      if (!silent && pendingList.length > prevPendingCount.current) {
        const diff = pendingList.length - prevPendingCount.current;
        toast.info(`${diff} nova${diff > 1 ? "s" : ""} pré-reserva${diff > 1 ? "s" : ""} recebida${diff > 1 ? "s" : ""}!`, { duration: 5000 });
        try {
          if ("Notification" in window && Notification.permission === "granted") {
            new Notification("Apecatu · Nova pré-reserva", {
              body: `${pendingList[0]?.name || "Cliente"} solicitou ${pendingList[0]?.cabin || pendingList[0]?.package || "uma vaga"}`,
              icon: "/assets/apecatu-logo.jpg",
            });
          }
        } catch (e) { /* ignore */ }
      }
      prevPendingCount.current = pendingList.length;
      setPending(pendingList);
      setHistory(historyList);
    } catch (e) {
      handleAuthError(e);
    }
  }, [handleAuthError]);

  useEffect(() => {
    if (!authed) { setLoading(false); return; }
    (async () => {
      await Promise.all([loadAvailability(), loadReservations(true)]);
      setLoading(false);
    })();

    // Request browser notification permission
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }

    // Poll every 20s for new reservations
    const timer = setInterval(() => loadReservations(false), POLL_INTERVAL);
    return () => clearInterval(timer);
  }, [authed, loadAvailability, loadReservations]);

  useEffect(() => {
    const entry = availability[selectedKey];
    // New format: {occupancy, restrictions} | Legacy: raw occupancy dict
    const saved = entry?.occupancy || (entry && !entry.occupancy ? entry : null);
    const savedRestr = entry?.restrictions;
    setCurrent(
      saved
        ? {
            cabineCasal: [...(saved.cabineCasal || [false, false])],
            suiteTripla: [...(saved.suiteTripla || [false, false, false])],
            suiteCasal: [...(saved.suiteCasal || [false, false])],
            cabineQuadrupla: [...(saved.cabineQuadrupla || [false, false, false, false])],
          }
        : emptyOcc()
    );
    setCurrentRestr({
      suiteTripla: savedRestr?.suiteTripla ?? null,
      cabineQuadruplaA: savedRestr?.cabineQuadruplaA ?? null,
      cabineQuadruplaB: savedRestr?.cabineQuadruplaB ?? null,
    });
  }, [selectedKey, availability]);

  const toggleBed = (cabinKey, idx) => {
    const isShared = cabinKey === "suiteTripla" || cabinKey === "cabineQuadrupla";
    setCurrent((prev) => {
      const next = { ...prev, [cabinKey]: [...prev[cabinKey]] };
      const v = next[cabinKey][idx];
      if (isShared) {
        // cycle: Livre (false) → "M" → "F" → Ocupada (true) → Livre
        if (!v) next[cabinKey][idx] = "M";
        else if (v === "M") next[cabinKey][idx] = "F";
        else if (v === "F") next[cabinKey][idx] = true;
        else next[cabinKey][idx] = false;
      } else {
        next[cabinKey][idx] = !v;
      }
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/availability`, { key: selectedKey, occupancy: current, restrictions: currentRestr }, { headers: authHeaders() });
      setAvailability({ ...availability, [selectedKey]: { occupancy: current, restrictions: currentRestr } });
      toast.success("Vagas salvas com sucesso");
    } catch (e) {
      if (!handleAuthError(e)) toast.error("Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const CABIN_LABELS = {
    cabineCasal: "Cabine Casal",
    suiteCasal: "Suíte Casal",
    suiteTripla: "Suíte Tripla",
    cabineQuadruplaA: "Quádrupla · Lado A",
    cabineQuadruplaB: "Quádrupla · Lado B",
  };
  const GENDER_LABELS = { M: "masculino", F: "feminino", null: "livre", undefined: "livre" };

  const handleParseText = async () => {
    if (!textInput.trim()) return;
    setParsing(true);
    try {
      const res = await axios.post(
        `${API}/availability/parse-text`,
        { text: textInput, known_keys: flatDates.map((d) => d.key) },
        { headers: authHeaders() }
      );
      setParseResult(res.data);
      console.log(res.data);
      setSelected(new Set(res.data.aplicaveis.map((_, i) => i)));
    } catch (e) {
      if (!handleAuthError(e)) toast.error("Erro ao analisar o texto");
    } finally {
      setParsing(false);
    }
  };

  const toggleSelected = (idx) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const handleApplyText = async () => {
    if (!parseResult) return;
    const changes = parseResult.aplicaveis
      .filter((_, i) => selected.has(i))
      .map((r) => ({
        date_key: r.date_key,
        cabin_key: r.cabin_key,
        free_count: r.free_count,
        total_beds: r.total_beds,
        gender: r.gender,
      }));
    if (changes.length === 0) return;
    setApplying(true);
    try {
      const res = await axios.post(`${API}/availability/apply-text`, { changes }, { headers: authHeaders() });
      toast.success(`${res.data.applied} atualização(ões) aplicada(s)`);
      setTextInput("");
      setParseResult(null);
      setSelected(new Set());
      await loadAvailability();
    } catch (e) {
      if (!handleAuthError(e)) toast.error("Erro ao aplicar mudanças");
    } finally {
      setApplying(false);
    }
  };

  const handleReset = () => {
    setCurrent(emptyOcc());
    setCurrentRestr(emptyRestrictions());
  };

  const setRestriction = (key, value) => {
    setCurrentRestr((prev) => ({ ...prev, [key]: prev[key] === value ? null : value }));
  };

  const approvePending = async (r) => {
    if (!window.confirm(`Aprovar a pré-reserva de ${r.name}? As camas do quarto "${r.cabin || "?"}" serão marcadas como ocupadas.`)) return;
    setBusyId(r.id);
    try {
      const res = await axios.post(`${API}/reservations/${r.id}/approve`, null, { headers: authHeaders() });
      toast.success(`Aprovado. ${res.data.beds_marked || 0} camas marcadas.`);
      await Promise.all([loadAvailability(), loadReservations(true)]);
    } catch (e) {
      if (!handleAuthError(e)) toast.error("Erro ao aprovar");
    } finally {
      setBusyId(null);
    }
  };

  const rejectPending = async (r) => {
    if (!window.confirm(`Recusar a pré-reserva de ${r.name}?`)) return;
    setBusyId(r.id);
    try {
      await axios.post(`${API}/reservations/${r.id}/reject`, null, { headers: authHeaders() });
      toast.success("Pré-reserva recusada");
      await loadReservations(true);
    } catch (e) {
      if (!handleAuthError(e)) toast.error("Erro ao recusar");
    } finally {
      setBusyId(null);
    }
  };

  const deleteReservation = async (r) => {
    if (!window.confirm(`Excluir permanentemente esta reserva?`)) return;
    setBusyId(r.id);
    try {
      await axios.delete(`${API}/reservations/${r.id}`, { headers: authHeaders() });
      toast.success("Excluída");
      await loadReservations(true);
    } catch (e) {
      if (!handleAuthError(e)) toast.error("Erro ao excluir");
    } finally {
      setBusyId(null);
    }
  };

  const totalTaken = Object.values(current).reduce((s, arr) => s + arr.filter(Boolean).length, 0);

  const goHome = () => {
    // Navigate back to homepage (support both hash and path routing)
    window.location.href = "/";
  };

  const handleLogout = () => {
    setToken("");
    setAuthed(false);
    toast.success("Sessão encerrada");
  };

  // ==== Not authenticated: show login screen ====
  if (!authed) {
    return <LoginScreen onSuccess={() => setAuthed(true)} />;
  }

  return (
    <div className="min-h-screen" style={{ background: "#F5F8FC", color: "#0F1B33" }}>
      <Toaster position="top-right" richColors />

      {/* Header */}
      <header className="border-b sticky top-0 z-20" style={{ borderColor: "#E5E9F0", background: "#fff" }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={goHome}
              className="w-9 h-9 flex items-center justify-center border hover:bg-gray-50"
              style={{ borderColor: "#E5E9F0" }}
              data-testid="back-home-btn"
            >
              <ArrowLeft size={16} />
            </button>
            <div>
              <div className="text-[10px] tracking-[0.22em] uppercase font-semibold" style={{ color: "#0066B3" }}>
                Painel Interno
              </div>
              <div className="text-xl font-bold" style={{ letterSpacing: "-0.02em" }}>
                Apecatu · Abrolhos 2026/27
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setTab("pending")}
              className="relative px-3 py-2 text-xs uppercase tracking-wider font-semibold border hover:bg-gray-50 flex items-center gap-2"
              style={{
                borderColor: tab === "pending" ? "#0066B3" : "#E5E9F0",
                background: tab === "pending" ? "#0066B3" : "#fff",
                color: tab === "pending" ? "#fff" : "#0F1B33",
              }}
              data-testid="tab-pending"
            >
              <Bell size={13} /> Pré-reservas
              {pending.length > 0 && (
                <span
                  className="absolute -top-2 -right-2 min-w-[20px] h-5 flex items-center justify-center text-[10px] px-1.5 font-black"
                  style={{ background: "#E05548", color: "#fff", borderRadius: "10px" }}
                  data-testid="pending-badge"
                >
                  {pending.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setTab("editor")}
              className="px-3 py-2 text-xs uppercase tracking-wider font-semibold border hover:bg-gray-50"
              style={{
                borderColor: tab === "editor" ? "#0066B3" : "#E5E9F0",
                background: tab === "editor" ? "#0066B3" : "#fff",
                color: tab === "editor" ? "#fff" : "#0F1B33",
              }}
              data-testid="tab-editor"
            >
              Editor de vagas
            </button>
            <button
              onClick={() => setTab("texto")}
              className="px-3 py-2 text-xs uppercase tracking-wider font-semibold border hover:bg-gray-50"
              style={{
                borderColor: tab === "texto" ? "#0066B3" : "#E5E9F0",
                background: tab === "texto" ? "#0066B3" : "#fff",
                color: tab === "texto" ? "#fff" : "#0F1B33",
              }}
              data-testid="tab-texto"
            >
              Atualizar por Texto
            </button>
            <button
              onClick={() => setTab("history")}
              className="px-3 py-2 text-xs uppercase tracking-wider font-semibold border hover:bg-gray-50"
              style={{
                borderColor: tab === "history" ? "#0066B3" : "#E5E9F0",
                background: tab === "history" ? "#0066B3" : "#fff",
                color: tab === "history" ? "#fff" : "#0F1B33",
              }}
              data-testid="tab-history"
            >
              Histórico
            </button>
            <button
              onClick={handleLogout}
              className="px-3 py-2 flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold border hover:bg-red-50"
              style={{ borderColor: "#E5E9F0", color: "#E05548" }}
              data-testid="logout-btn"
              title="Sair do painel"
            >
              <LogOut size={13} /> Sair
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* ========= PENDING RESERVATIONS TAB ========= */}
        {tab === "pending" && (
          <section data-testid="pending-section">
            <div className="mb-6">
              <div className="text-[10px] tracking-[0.22em] uppercase font-semibold" style={{ color: "#0066B3" }}>
                Pré-reservas aguardando aprovação
              </div>
              <h2 className="text-2xl font-bold mt-1" style={{ letterSpacing: "-0.02em" }}>
                {pending.length === 0 ? "Nenhuma pré-reserva pendente" : `${pending.length} pré-reserva${pending.length > 1 ? "s" : ""} para revisar`}
              </h2>
              <p className="text-xs mt-1" style={{ color: "#5A6B85" }}>
                Verificando novas a cada 20 segundos. Aprove após confirmar o pagamento.
              </p>
            </div>

            {loading ? (
              <div className="text-sm text-gray-500 p-8 text-center">Carregando...</div>
            ) : pending.length === 0 ? (
              <div className="p-10 text-center border" style={{ background: "#fff", borderColor: "#E5E9F0" }}>
                <Bell size={32} className="mx-auto mb-3" style={{ color: "#B8C4D8" }} />
                <div className="text-sm" style={{ color: "#5A6B85" }}>
                  Tudo em dia. Novas pré-reservas aparecem aqui automaticamente.
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {pending.map((r) => (
                  <div key={r.id} className="p-5 border grid md:grid-cols-12 gap-4" style={{ background: "#fff", borderColor: "#F5C754", borderLeftWidth: 4 }} data-testid={`pending-card-${r.id}`}>
                    <div className="md:col-span-8">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] px-2 py-0.5 font-bold uppercase tracking-wider" style={{ background: "#FFF4D6", color: "#8A6D1E" }}>
                          <Clock size={9} className="inline mr-1" /> Pendente
                        </span>
                        <span className="text-xs" style={{ color: "#5A6B85" }}>
                          {new Date(r.created_at).toLocaleString("pt-BR")}
                        </span>
                      </div>
                      <div className="text-lg font-bold" style={{ letterSpacing: "-0.01em" }}>{r.name}</div>
                      <div className="text-xs mt-2 grid sm:grid-cols-2 gap-y-1" style={{ color: "#5A6B85" }}>
                        <span className="flex items-center gap-1.5"><Phone size={11} /> {r.phone}</span>
                        {r.email && <span className="flex items-center gap-1.5"><Mail size={11} /> {r.email}</span>}
                        <span className="flex items-center gap-1.5"><Users size={11} /> {r.pax} pessoa{r.pax > 1 ? "s" : ""}</span>
                        <span className="flex items-center gap-1.5"><Anchor size={11} /> {r.cabin || "Sem preferência"}</span>
                      </div>
                      <div className="mt-3 grid sm:grid-cols-2 gap-2 text-xs">
                        <div className="p-2" style={{ background: "#F5F8FC" }}>
                          <div className="text-[9px] uppercase tracking-wider font-bold" style={{ color: "#0066B3" }}>Pacote</div>
                          <div className="mt-0.5">{r.package}</div>
                        </div>
                        <div className="p-2" style={{ background: "#F5F8FC" }}>
                          <div className="text-[9px] uppercase tracking-wider font-bold" style={{ color: "#0066B3" }}>Data</div>
                          <div className="mt-0.5">{r.travel_date || "A definir"}</div>
                        </div>
                        {r.dive_level && (
                          <div className="p-2 sm:col-span-2" style={{ background: "#EFF6FF" }}>
                            <div className="text-[9px] uppercase tracking-wider font-bold" style={{ color: "#0066B3" }}>Nível de mergulho</div>
                            <div className="mt-0.5 font-semibold">{r.dive_level}</div>
                          </div>
                        )}
                        {r.dietary_restriction && (
                          <div className="p-2 sm:col-span-2" style={{ background: "#FEF3C7" }}>
                            <div className="text-[9px] uppercase tracking-wider font-bold" style={{ color: "#B45309" }}>Restrição alimentar</div>
                            <div className="mt-0.5 font-semibold">{r.dietary_restriction}</div>
                          </div>
                        )}
                        {r.has_paid !== null && r.has_paid !== undefined && (
                          <div className="p-2 sm:col-span-2" style={{ background: r.has_paid ? "#DCFCE7" : "#FEE2E2" }}>
                            <div className="text-[9px] uppercase tracking-wider font-bold" style={{ color: r.has_paid ? "#166534" : "#991B1B" }}>Pagamento</div>
                            <div className="mt-0.5 font-semibold flex items-center gap-2">
                              {r.has_paid ? "✅ Já pagou" : "⏳ Ainda não pagou"}
                              {r.has_paid && r.receipt_url && (
                                <a
                                  href={r.receipt_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="ml-auto px-2 py-1 text-[10px] uppercase tracking-wider font-bold hover:opacity-80"
                                  style={{ background: "#0066B3", color: "#fff" }}
                                  data-testid={`view-receipt-${r.id}`}
                                >
                                  Ver comprovante
                                </a>
                              )}
                            </div>
                            {r.has_paid && (r.paid_divers != null || r.paid_companions != null) && (
                              <div className="mt-1.5 text-[11px]" style={{ color: "#166534" }}>
                                Composição: <strong>{r.paid_divers || 0} mergulhador(es)</strong> + <strong>{r.paid_companions || 0} acompanhante(s)</strong>
                              </div>
                            )}
                            {r.has_paid && (r.birth_date || r.cpf) && (
                              <div className="mt-1 text-[11px]" style={{ color: "#166534" }}>
                                {r.birth_date && <>Nasc: <strong>{r.birth_date}</strong></>}
                                {r.birth_date && r.cpf && " · "}
                                {r.cpf && <>CPF: <strong>{r.cpf}</strong></>}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      {r.message && (
                        <div className="mt-2 p-2 text-xs italic" style={{ background: "#FFFBF0", color: "#5A6B85" }}>
                          "{r.message}"
                        </div>
                      )}
                    </div>

                    <div className="md:col-span-4 flex flex-col gap-2 justify-center">
                      <a
                        href={`https://wa.me/${r.phone.replace(/\D/g, "")}?text=${encodeURIComponent(`Olá ${r.name}! Recebi sua pré-reserva Abrolhos, aqui é o Lucas da Apecatu.`)}`}
                        target="_blank"
                        rel="noreferrer"
                        className="w-full py-2 flex items-center justify-center gap-2 text-xs uppercase tracking-wider font-semibold border hover:bg-gray-50"
                        style={{ borderColor: "#25D366", color: "#075E54" }}
                        data-testid={`whatsapp-client-${r.id}`}
                      >
                        <MessageCircle size={13} /> Falar no WhatsApp
                      </a>
                      <button
                        onClick={() => approvePending(r)}
                        disabled={busyId === r.id}
                        className="w-full py-2 flex items-center justify-center gap-2 text-xs uppercase tracking-wider font-bold disabled:opacity-50"
                        style={{ background: "#1FA866", color: "#fff" }}
                        data-testid={`approve-btn-${r.id}`}
                      >
                        <Check size={13} /> Aprovar reserva
                      </button>
                      <button
                        onClick={() => rejectPending(r)}
                        disabled={busyId === r.id}
                        className="w-full py-2 flex items-center justify-center gap-2 text-xs uppercase tracking-wider font-semibold disabled:opacity-50"
                        style={{ background: "#fff", color: "#E05548", border: "1px solid #E05548" }}
                        data-testid={`reject-btn-${r.id}`}
                      >
                        <X size={13} /> Recusar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* ========= HISTORY TAB ========= */}
        {tab === "history" && (
          <section data-testid="history-section">
            <div className="mb-6">
              <div className="text-[10px] tracking-[0.22em] uppercase font-semibold" style={{ color: "#0066B3" }}>
                Histórico
              </div>
              <h2 className="text-2xl font-bold mt-1" style={{ letterSpacing: "-0.02em" }}>
                Reservas processadas ({history.length})
              </h2>
            </div>
            {history.length === 0 ? (
              <div className="p-10 text-center border text-sm" style={{ background: "#fff", borderColor: "#E5E9F0", color: "#5A6B85" }}>
                Ainda sem histórico.
              </div>
            ) : (
              <div className="space-y-2">
                {history.map((r) => {
                  const isApproved = r.status === "approved";
                  return (
                    <div key={r.id} className="p-4 border grid md:grid-cols-12 gap-3 items-center" style={{ background: "#fff", borderColor: "#E5E9F0" }} data-testid={`history-card-${r.id}`}>
                      <div className="md:col-span-2">
                        <span className="text-[10px] px-2 py-0.5 font-bold uppercase tracking-wider" style={{
                          background: isApproved ? "#EAF5EE" : "#FFEBE9",
                          color: isApproved ? "#0F6E3F" : "#8A2F27",
                        }}>
                          {isApproved ? "Aprovada" : r.status}
                        </span>
                      </div>
                      <div className="md:col-span-5">
                        <div className="font-bold text-sm">{r.name}</div>
                        <div className="text-xs" style={{ color: "#5A6B85" }}>
                          {r.package} · {r.travel_date || "sem data"} · {r.cabin || "sem preferência"}
                        </div>
                      </div>
                      <div className="md:col-span-3 text-xs" style={{ color: "#5A6B85" }}>
                        {r.phone} · {r.pax}p
                      </div>
                      <div className="md:col-span-2 text-right">
                        <button
                          onClick={() => deleteReservation(r)}
                          disabled={busyId === r.id}
                          className="text-xs underline"
                          style={{ color: "#E05548" }}
                          data-testid={`delete-btn-${r.id}`}
                        >
                          excluir
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {/* ========= EDITOR TAB ========= */}
        {tab === "editor" && (
          <div className="grid md:grid-cols-12 gap-6" data-testid="editor-section">
            <aside className="md:col-span-4">
              <div className="text-[10px] tracking-[0.22em] uppercase font-semibold mb-3" style={{ color: "#0066B3" }}>
                Datas {adminYear}
              </div>
              <div className="flex gap-2 mb-3" data-testid="admin-year-tabs">
                {YEARS.map((y) => (
                  <button
                    key={y}
                    onClick={() => {
                      setAdminYear(y);
                      const first = flatDates.find((d) => d.year === y);
                      if (first) setSelectedKey(first.key);
                    }}
                    className="px-4 py-1.5 text-xs font-semibold border transition"
                    style={{
                      background: adminYear === y ? "#0066B3" : "#fff",
                      color: adminYear === y ? "#fff" : "#5A6B85",
                      borderColor: adminYear === y ? "#0066B3" : "#E5E9F0",
                    }}
                    data-testid={`admin-year-tab-${y}`}
                  >
                    {y}
                  </button>
                ))}
              </div>
              <div className="space-y-1 max-h-[calc(100vh-260px)] overflow-y-auto thin-scrollbar pr-2" data-testid="date-list">
                {flatDates.filter((d) => d.year === adminYear).map((d) => {
                  const savedEntry = availability[d.key];
                  const savedOcc = savedEntry?.occupancy || (savedEntry && !savedEntry.occupancy ? savedEntry : null);
                  const taken = savedOcc
                    ? Object.values(savedOcc).reduce((s, arr) => s + (Array.isArray(arr) ? arr.filter(Boolean).length : 0), 0)
                    : 0;
                  const free = 11 - taken;
                  const isSelected = d.key === selectedKey;
                  return (
                    <button
                      key={d.key}
                      onClick={() => setSelectedKey(d.key)}
                      className={`w-full text-left p-3 border transition ${isSelected ? "" : "hover:bg-white"}`}
                      style={{
                        background: isSelected ? "#0066B3" : "#fff",
                        color: isSelected ? "#fff" : "#0F1B33",
                        borderColor: isSelected ? "#0066B3" : "#E5E9F0",
                      }}
                      data-testid={`date-item-${d.year}-${d.month}-${d.range.replace(/[^\w]/g, "")}`}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="text-xs opacity-75">{d.month} · {d.days}d</div>
                          <div className="font-semibold text-sm">{d.range}</div>
                        </div>
                        <div
                          className="text-xs px-2 py-1 font-bold"
                          style={{
                            background: isSelected ? "rgba(255,255,255,0.2)" : free === 0 ? "#FFEBE9" : free < 11 ? "#FFF7E0" : "#EAF5EE",
                            color: isSelected ? "#fff" : free === 0 ? "#E05548" : free < 11 ? "#8A6D1E" : "#1FA866",
                          }}
                        >
                          {free}/11
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </aside>

            <section className="md:col-span-8">
              {selectedDate ? (
                <div className="p-6 border" style={{ background: "#fff", borderColor: "#E5E9F0" }}>
                  <div className="flex items-start justify-between mb-6 pb-4 border-b" style={{ borderColor: "#E5E9F0" }}>
                    <div>
                      <div className="text-[10px] tracking-[0.22em] uppercase font-semibold" style={{ color: "#0066B3" }}>
                        Editar disponibilidade
                      </div>
                      <div className="text-2xl font-bold mt-1" style={{ letterSpacing: "-0.02em" }}>
                        {selectedDate.range} · {selectedDate.month}/{selectedDate.year}
                      </div>
                      <div className="text-xs mt-1" style={{ color: "#5A6B85" }}>
                        {selectedDate.days} dias · {11 - totalTaken} vagas livres de 11
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={handleReset}
                        className="px-4 py-2 flex items-center gap-2 text-xs uppercase tracking-wider font-semibold border hover:bg-gray-50"
                        style={{ borderColor: "#E5E9F0", color: "#5A6B85" }}
                        data-testid="reset-btn"
                      >
                        <RotateCcw size={14} /> Limpar
                      </button>
                      <button
                        onClick={handleSave}
                        disabled={saving}
                        className="px-5 py-2 flex items-center gap-2 text-xs uppercase tracking-wider font-semibold disabled:opacity-60"
                        style={{ background: "#0066B3", color: "#fff" }}
                        data-testid="save-btn"
                      >
                        <Save size={14} /> {saving ? "Salvando..." : "Salvar"}
                      </button>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    {CABINS.map((c) => {
                      const isShared = c.key === "suiteTripla" || c.key === "cabineQuadrupla";
                      const isQuadCab = c.key === "cabineQuadrupla";
                      return (
                        <div key={c.key} className="p-4 border" style={{ borderColor: "#E5E9F0" }} data-testid={`cabin-editor-${c.key}`}>
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <div className="font-bold">{c.label}</div>
                              <div className="text-xs" style={{ color: "#5A6B85" }}>
                                {current[c.key].filter((x) => !x).length}/{c.beds} livre{c.beds > 1 ? "s" : ""}
                                {isShared && " · clique nas camas p/ marcar M / F / Ocupada"}
                              </div>
                            </div>
                          </div>
                          <div className="grid grid-cols-4 gap-2">
                            {current[c.key].map((v, i) => {
                              const label = v === "M" ? "M" : v === "F" ? "F" : v ? "Ocupada" : "Livre";
                              const bg = v === "M" ? "#3B82F6" : v === "F" ? "#EC4899" : v ? "#E05548" : "#1FA866";
                              const border = v === "M" ? "#1E40AF" : v === "F" ? "#9F1239" : v ? "#8A2F27" : "#0F6E3F";
                              const isQuad = c.key === "cabineQuadrupla";
                              const side = isQuad ? (i < 2 ? "A" : "B") : null;
                              return (
                                <button
                                  key={i}
                                  onClick={() => toggleBed(c.key, i)}
                                  className="p-3 text-center font-bold text-xs uppercase tracking-wider transition relative"
                                  style={{
                                    background: bg,
                                    color: "#fff",
                                    border: `2px solid ${border}`,
                                    borderRightWidth: isQuad && i === 1 ? 4 : 2,
                                    marginRight: isQuad && i === 1 ? 6 : 0,
                                  }}
                                  data-testid={`bed-toggle-${c.key}-${i}`}
                                >
                                  {side && (
                                    <span
                                      className="absolute top-0.5 left-1 text-[8px] font-black"
                                      style={{ color: "rgba(255,255,255,0.75)" }}
                                    >
                                      Lado {side}
                                    </span>
                                  )}
                                  {label}
                                  <div className="text-[9px] mt-1 opacity-80">Cama {i + 1}</div>
                                </button>
                              );
                            })}
                          </div>

                          {/* Restrição de gênero manual (para cabines compartilhadas) */}
                          {isShared && (
                            <div className="mt-3 pt-3 border-t" style={{ borderColor: "#E5E9F0" }}>
                              <div className="text-[10px] uppercase tracking-wider font-bold mb-2" style={{ color: "#0066B3" }}>
                                Restringir vagas livres a
                              </div>
                              {isQuadCab ? (
                                <div className="space-y-2">
                                  {[
                                    { key: "cabineQuadruplaA", label: "Lado A (camas 1-2)" },
                                    { key: "cabineQuadruplaB", label: "Lado B (camas 3-4)" },
                                  ].map((side) => (
                                    <div key={side.key} className="flex items-center justify-between gap-2">
                                      <div className="text-[11px] font-semibold" style={{ color: "#0F1B33" }}>{side.label}</div>
                                      <div className="flex gap-1">
                                        {[
                                          { val: null, label: "Livre", bg: "#F5F8FC", color: "#5A6B85", border: "#E5E9F0" },
                                          { val: "M", label: "Masculino", bg: "#DBEAFE", color: "#1E40AF", border: "#3B82F6" },
                                          { val: "F", label: "Feminino", bg: "#FCE7F3", color: "#9F1239", border: "#EC4899" },
                                        ].map((opt) => {
                                          const active = currentRestr[side.key] === opt.val;
                                          return (
                                            <button
                                              key={String(opt.val)}
                                              type="button"
                                              onClick={() => setCurrentRestr((prev) => ({ ...prev, [side.key]: opt.val }))}
                                              className="px-2 py-1 text-[10px] uppercase tracking-wider font-bold border"
                                              style={{
                                                background: active ? opt.border : opt.bg,
                                                color: active ? "#fff" : opt.color,
                                                borderColor: opt.border,
                                              }}
                                              data-testid={`restr-${side.key}-${opt.val || "none"}`}
                                            >
                                              {opt.label}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="flex items-center justify-between gap-2">
                                  <div className="text-[11px] font-semibold" style={{ color: "#0F1B33" }}>Cabine inteira</div>
                                  <div className="flex gap-1">
                                    {[
                                      { val: null, label: "Livre", bg: "#F5F8FC", color: "#5A6B85", border: "#E5E9F0" },
                                      { val: "M", label: "Masculino", bg: "#DBEAFE", color: "#1E40AF", border: "#3B82F6" },
                                      { val: "F", label: "Feminino", bg: "#FCE7F3", color: "#9F1239", border: "#EC4899" },
                                    ].map((opt) => {
                                      const active = currentRestr.suiteTripla === opt.val;
                                      return (
                                        <button
                                          key={String(opt.val)}
                                          type="button"
                                          onClick={() => setCurrentRestr((prev) => ({ ...prev, suiteTripla: opt.val }))}
                                          className="px-2 py-1 text-[10px] uppercase tracking-wider font-bold border"
                                          style={{
                                            background: active ? opt.border : opt.bg,
                                            color: active ? "#fff" : opt.color,
                                            borderColor: opt.border,
                                          }}
                                          data-testid={`restr-suiteTripla-${opt.val || "none"}`}
                                        >
                                          {opt.label}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                              <div className="text-[10px] mt-2" style={{ color: "#5A6B85" }}>
                                A restrição aparece no site como <em>{'"X vaga(s) livre(s) masculina(s)/feminina(s)"'}</em>. Não ocupa camas.
                              </div>
                            </div>
                          )}

                          {c.key === "cabineQuadrupla" && (
                            <div className="text-[10px] mt-2" style={{ color: "#5A6B85" }}>
                              <strong>Lado A</strong> (camas 1-2) e <strong>Lado B</strong> (camas 3-4) são quartos separados. Cada lado tem seu próprio gênero.
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-6 p-4 text-xs" style={{ background: "#F5F8FC", color: "#5A6B85" }}>
                    <strong style={{ color: "#0F1B33" }}>Dica:</strong> ao aprovar uma pré-reserva na aba "Pré-reservas",
                    as camas do quarto escolhido pelo cliente são marcadas aqui automaticamente. Este editor é útil para
                    ajustes manuais ou para bloquear vagas por motivos internos.
                  </div>
                </div>
              ) : (
                <div className="p-6 text-center text-gray-500">Selecione uma data</div>
              )}
            </section>
          </div>
        )}
        {tab === "texto" && (
          <section data-testid="texto-section">
            <div className="mb-6">
              <div className="text-[10px] tracking-[0.22em] uppercase font-semibold" style={{ color: "#0066B3" }}>
                Atualizar vagas por texto
              </div>
              <h2 className="text-2xl font-bold mt-1" style={{ letterSpacing: "-0.02em" }}>
                Cole o texto da Apecatu no formato padrão
              </h2>
              <p className="text-xs mt-1" style={{ color: "#5A6B85" }}>
                Formato: <code>Mês intervalo - cabine - estado</code>, uma linha por cabine.
                Ex: <code>Setembro 11 a 13 - quad a - 1 livre masc</code>
              </p>
            </div>

            <textarea
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              rows={10}
              className="w-full p-3 text-sm font-mono border outline-none mb-3"
              style={{ borderColor: "#E5E9F0" }}
              placeholder={"Setembro 11 a 13 - quad a - 1 livre masc\nSetembro 11 a 13 - quad b - 2 livres masc\n..."}
              data-testid="texto-input"
            />

            <button
              onClick={handleParseText}
              disabled={parsing || !textInput.trim()}
              className="px-5 py-2 text-xs uppercase tracking-wider font-semibold disabled:opacity-60"
              style={{ background: "#0066B3", color: "#fff" }}
              data-testid="texto-analisar-btn"
            >
              {parsing ? "Analisando..." : "Analisar"}
            </button>

            {parseResult && (
              <div className="mt-6 space-y-2">
                {parseResult.aplicaveis.length > 0 && (
                  <>
                    <div className="text-xs font-bold uppercase tracking-wider mt-4 mb-2" style={{ color: "#1FA866" }}>
                      Mudanças reconhecidas ({parseResult.aplicaveis.length})
                    </div>
                    {parseResult.aplicaveis.map((r, i) => (
                      <label
                        key={i}
                        className="flex items-center gap-3 p-3 border cursor-pointer"
                        style={{
                          borderColor: r.inferred ? "#F5C754" : "#E5E9F0",
                          background: r.inferred ? "#FFFBF0" : "#fff",
                        }}
                        data-testid={`texto-item-${i}`}
                      >
                        <input type="checkbox" checked={selected.has(i)} onChange={() => toggleSelected(i)} />
                        <div className="text-sm">
                          {r.inferred && (
                            <span className="text-[9px] px-1.5 py-0.5 mr-1.5 font-bold uppercase" style={{ background: "#F5C754", color: "#5A4300" }}>
                              Inferido
                            </span>
                          )}
                          <strong>{r.date_key.replace("|", " · ")}</strong> — {CABIN_LABELS[r.cabin_key]}:{" "}
                          {r.before.livres}/{r.before.total} livres
                          {r.before.genero !== undefined && ` (${GENDER_LABELS[r.before.genero]})`}
                          {" → "}
                          <strong>{r.after.livres}/{r.after.total} livres{r.after.genero !== undefined && ` (${GENDER_LABELS[r.after.genero]})`}</strong>
                          {r.inferred && (
                            <div className="text-[10px] mt-0.5" style={{ color: "#8A6D1E" }}>
                              Não mencionada no texto para essa data — assumindo ocupada.
                            </div>
                          )}
                        </div>
                      </label>
                    ))}
                  </>
                )}

                {parseResult.problemas.length > 0 && (
                  <>
                    <div className="text-xs font-bold uppercase tracking-wider mt-4 mb-2" style={{ color: "#E05548" }}>
                      Não reconhecidas ({parseResult.problemas.length})
                    </div>
                    {parseResult.problemas.map((r, i) => (
                      <div key={i} className="p-3 border text-sm" style={{ borderColor: "#FFCFC9", background: "#FFF5F4" }}>
                        <div className="font-mono text-xs" style={{ color: "#8A2F27" }}>{r.raw}</div>
                        <div className="text-xs mt-1" style={{ color: "#5A6B85" }}>{r.reason}</div>
                      </div>
                    ))}
                  </>
                )}

                {parseResult.aplicaveis.length > 0 && (
                  <button
                    onClick={handleApplyText}
                    disabled={applying || selected.size === 0}
                    className="mt-4 px-5 py-2 text-xs uppercase tracking-wider font-bold disabled:opacity-60"
                    style={{ background: "#1FA866", color: "#fff" }}
                    data-testid="texto-aplicar-btn"
                  >
                    {applying ? "Aplicando..." : `Aplicar selecionadas (${selected.size})`}
                  </button>
                )}
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
};

export default VagasAdmin;